import React, { useState } from "react";
import { LogIn, Loader2, X, HelpCircle, Lock, Mail } from "lucide-react"; 
import toast, { Toaster } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import logo from "../../assets/logo.png";

const Header = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
      <div className="flex items-center space-x-3">
        {/* Logo Image + Clean Brand Name Text */}
        <div className="flex items-center gap-2.5">
          <img src={logo} alt="Indiv TechnoSoft" className="h-8 w-auto object-contain" />
          <h1 className="text-base font-bold text-slate-800 tracking-wide">
            Indiv <span className="text-orange-500 font-semibold">TechnoSoft</span>
          </h1>
        </div>
      </div>
    </div>
  </header>
);

const AdminLogin = ({ setIsAuthenticated }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [supportEmail, setSupportEmail] = useState("");
  const [supportBody, setSupportBody] = useState("Hi Support team, I need assistance logging into the admin portal.");

  const navigate = useNavigate();
  const location = useLocation();
  const API_LOGIN = process.env.REACT_APP_API_LOGIN;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await axios.post(API_LOGIN, { email, password }, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      if (res.data?.token) {
        localStorage.setItem("admin-auth", res.data.token);
        setIsAuthenticated(true);
        navigate(location.state?.from || "/", { replace: true });
      } else {
        const msg = res.data?.error || "Invalid email or password.";
        toast.error(msg);
        setError(msg);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Unable to connect to server. Please try again.";
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden font-sans bg-slate-50">
      {/* BACKGROUND: Clean modern grid pattern */}
      <div className="absolute inset-0 z-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-60"></div>
      
      {/* Accent Blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-orange-400/10 rounded-full blur-3xl"></div>

      <Toaster position="top-right" />
      <Header />

      {/* Main Container */}
      <div className="relative z-10 w-full max-w-4xl mx-4 flex flex-col md:flex-row items-stretch bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden mt-12">
        
        {/* Left Panel - Information Side */}
        <div className="hidden md:flex flex-col justify-between flex-1 p-12 bg-gradient-to-br from-slate-900 to-indigo-950 text-white relative">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]"></div>

          <div className="relative z-10 my-auto py-8">
            <h2 className="text-3xl font-bold tracking-tight mb-4">
              Management & <br />
              <span className="text-orange-400">Control Center</span>
            </h2>
            <p className="text-slate-300 text-sm leading-relaxed max-w-sm">
              Log in to manage team members, portfolio projects, inquiries, and recruitment applications from one central panel.
            </p>
          </div>

          <div className="relative z-10 border-t border-slate-800 pt-6">
            <p className="text-xs font-medium text-slate-400">
              © {new Date().getFullYear()} Indiv TechnoSoft. All rights reserved.
            </p>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="p-8 sm:p-12 w-full md:w-[440px] bg-white flex flex-col justify-center">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-1">
              Sign In
            </h2>
            <p className="text-xs text-slate-500">
              Enter your credentials to access the admin portal.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-xs font-medium animate-shake">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Mail size={14} className="text-slate-400" /> Email Address
              </label>
              <input
                type="email"
                placeholder="admin@indivtechnosoft.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none font-medium text-sm text-slate-800 placeholder:text-slate-400"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Lock size={14} className="text-slate-400" /> Password
              </label>
              <input
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:border-blue-600 focus:bg-white transition-all outline-none font-medium text-sm text-slate-800 placeholder:text-slate-400"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 mt-2 bg-blue-600 hover:bg-blue-700 active:scale-[0.99] text-white font-semibold text-sm rounded-xl flex items-center justify-center gap-2 shadow-md shadow-blue-600/10 transition-all disabled:opacity-60 group"
            >
              {loading ? (
                <Loader2 className="animate-spin w-4 h-4" />
              ) : (
                <LogIn className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              )}
              {loading ? "Signing in..." : "Sign In"}
            </button>

            <button
              type="button"
              onClick={() => { setIsModalOpen(true); setSupportEmail(email); }}
              className="w-full text-center text-xs text-slate-500 hover:text-slate-800 transition-colors font-medium mt-4 flex items-center justify-center gap-1.5"
            >
              <HelpCircle size={14} /> Need help signing in?
            </button>
          </form>
        </div>
      </div>

      {/* Support Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex justify-between items-center mb-5">
              <div>
                <h2 className="text-base font-bold text-slate-900">Contact Support</h2>
                <p className="text-xs text-slate-500">Send an email to request login assistance</p>
              </div>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Your Email</label>
                <input
                  type="email"
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                  className="w-full px-3.5 py-2 border border-slate-200 rounded-xl outline-none focus:border-blue-600 font-medium text-sm bg-slate-50 text-slate-800"
                  placeholder="name@company.com"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Message</label>
                <textarea
                  value={supportBody}
                  onChange={(e) => setSupportBody(e.target.value)}
                  rows="4"
                  className="w-full px-3.5 py-2 border border-slate-200 rounded-xl outline-none focus:border-blue-600 resize-none font-medium text-sm bg-slate-50 text-slate-700"
                />
              </div>

              <button
                onClick={() => {
                   const subject = "Admin Portal Login Support";
                   window.location.href = `mailto:info@indivtechnosoft.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(supportBody)}`;
                   setIsModalOpen(false);
                }}
                className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm rounded-xl transition-colors shadow-sm"
              >
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.2s ease-in-out 2;
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;